/**
 * Home prompts: what is waiting on the office, at the very top of Home, so
 * nobody glancing at the page can miss it (owner 2026-10-20, showing the
 * Atlas dashboard: "this brings unseen sms, leads, missed calls and stuff
 * to the forefront ... this is more prominent"; "they don't have to match
 * but i need it to be obvious").
 *
 * Owner 2026-10-21 on the cards below: "way too big". Home now opens on ONE
 * slim strip (about 60px), rose when anything is urgent, orange otherwise:
 * a row of count chips ("17 missed calls", "82 voicemails", "27 texts",
 * sync and answer-rate problems too), each linking to its list with a
 * tooltip for the oldest wait and the office split. Its chevron opens the
 * detailed cards; open or closed is remembered per user
 * (`homeLayout.promptsExpanded`).
 *
 * The expanded view: full-width tinted cards, stacked:
 *
 *   1. PMS sync problems (an office's data stopped updating).
 *   2. The answer-rate dip.
 *   3. "N people are waiting on us": a row per kind (missed calls with no
 *      call back, voicemails nobody listened to, leads not contacted, portal
 *      messages, calls the call AI flagged, booking and portal requests),
 *      each with its count, the oldest wait, the per-office split and one
 *      click to the list.
 *   4. Unanswered patient texts, on their own card.
 *
 * The phone chips (missed calls, voicemails, calls flagged for follow-up,
 * after-hours AI calls) open their exact rows in a drawer on Home
 * (`?waiting=<key>`, WaitingList.tsx); texts open Messages filtered.
 *
 * Each card has a small per-user snooze in its corner; it comes back early
 * when something new arrives. Refreshed every minute and on live call,
 * lead and message events, with an optional pop-up for new urgent items.
 *
 * Counts only (GET /v1/shell/prompts, /v1/shell/freshness); names and
 * messages stay on the page each card opens, which audits its own read.
 */
import type { DashboardRole, HomePrompt, HomePromptKey } from '@velora/contracts/shell';
import { HomePromptsSchema } from '@velora/contracts/shell';
import { Link, useNavigate } from '@tanstack/react-router';
import {
  Button,
  formatNumber,
  Popover,
  relativeAge,
  toast,
  Tooltip,
  TooltipProvider,
} from '@velora/ui';
import {
  Bot,
  MessageSquareReply,
  PhoneForwarded,
  CalendarPlus,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Clock,
  DatabaseZap,
  Inbox,
  MessageSquare,
  PhoneMissed,
  TrendingDown,
  UserPlus,
  Voicemail,
} from 'lucide-react';
import { useEffect, useRef, type ReactNode } from 'react';
import { useFreshness } from '../../layout/TopBarParts.js';
import { useLive } from '../../live/LiveProvider.js';
import { useOfficeScope } from '../../office/OfficeScope.js';
import { useOfficeQuery } from '../../office/useOfficeQuery.js';
import { usePrefs } from '../../prefs/prefs.js';
import { usePermission } from '../../session/session.js';
import { ageLabel } from '../WaitingStrip.js';
import {
  CHIP_NOUN,
  chipTooltip,
  isSnoozed,
  newUrgent,
  officeSplitLabel,
  PROMPT_META,
  rankPrompts,
  snooze,
  SNOOZE_OPTIONS,
  snoozeUntil,
  stripSeverity,
  syncChipNoun,
  syncProblems,
  toCards,
  unsnoozeAll,
  waitingTitle,
  type Snoozable,
} from './prompts.js';

/** Refetched on live call, lead and message events too, not only on the timer. */
export const PROMPTS_QUERY_KEY = [
  'shell',
  'prompts',
  { live: ['call', 'lead', 'inbox.thread', 'booking_request', 'portal_request'] },
] as const;
/** Refresh cadence (Atlas polls its banners every 30 to 120 s). */
export const PROMPTS_REFRESH_MS = 60_000;
/** Snooze key of the PMS sync card (prompt keys are the rest). */
export const SYNC_CARD_KEY = 'pms_sync';

const ICON: Record<HomePromptKey, (size: number) => ReactNode> = {
  missed_calls: (s) => <PhoneMissed size={s} aria-hidden />,
  voicemails: (s) => <Voicemail size={s} aria-hidden />,
  leads: (s) => <UserPlus size={s} aria-hidden />,
  texts: (s) => <MessageSquare size={s} aria-hidden />,
  missed_call_replies: (s) => <MessageSquareReply size={s} aria-hidden />,
  portal_messages: (s) => <Inbox size={s} aria-hidden />,
  ai_calls: (s) => <PhoneForwarded size={s} aria-hidden />,
  after_hours_ai: (s) => <Bot size={s} aria-hidden />,
  answer_rate: (s) => <TrendingDown size={s} aria-hidden />,
  booking_requests: (s) => <CalendarPlus size={s} aria-hidden />,
  portal_requests: (s) => <ClipboardList size={s} aria-hidden />,
};

export function useHomePrompts(enabled = true) {
  useLive('call');
  useLive('lead');
  useLive('inbox.thread');
  return useOfficeQuery({
    queryKey: PROMPTS_QUERY_KEY,
    queryFn: (c, signal) => c.request('/shell/prompts', { schema: HomePromptsSchema, signal }),
    refetchInterval: PROMPTS_REFRESH_MS,
    // A hidden tab does not poll: it refetches when focused (stale by then),
    // and live call / lead / thread events still refresh it (2026-10-21).
    refetchIntervalInBackground: false,
    retry: false,
    enabled,
  });
}

/** "2 marked urgent · oldest 1d · 1 web chat" (no office split). */
function subline(p: HomePrompt, now = Date.now()): string | null {
  const parts: string[] = [];
  if (p.urgentCount > 0 && p.key !== 'answer_rate') {
    parts.push(
      p.key === 'booking_requests' || p.key === 'portal_requests'
        ? `${formatNumber(p.urgentCount)} past due`
        : `${formatNumber(p.urgentCount)} marked urgent`,
    );
  }
  if (p.oldestAt) parts.push(`oldest ${ageLabel(p.oldestAt, now)} ago`);
  if (p.detail) parts.push(p.detail);
  return parts.length ? parts.join(' · ') : null;
}

const keepOffice = (extra: Record<string, string>) =>
  ((prev: Record<string, unknown>) => ({ office: prev['office'], ...extra })) as never;

/** The small "Snooze" in a card's corner (per user; not the main affordance). */
function SnoozeCorner({
  label,
  onSnooze,
  inline = false,
}: {
  label: string;
  onSnooze: (until: Date) => void;
  /** In the strip's tool row instead of a card's corner. */
  inline?: boolean;
}) {
  return (
    <Popover
      label={`Snooze: ${label}`}
      align="end"
      trigger={
        <button
          type="button"
          className={inline ? 'hp-snooze-btn hp-snooze-btn--inline' : 'hp-snooze-btn'}
          aria-label={`Snooze: ${label}`}
          title="Snooze for me"
        >
          <Clock size={12} aria-hidden />
          <span className="hp-snooze-btn__label">Snooze</span>
        </button>
      }
    >
      <div className="hp-snooze" role="group" aria-label="Snooze for">
        <span className="hp-snooze__head">Snooze for me</span>
        {SNOOZE_OPTIONS.map((o) => (
          <Button
            key={o.id}
            variant="ghost"
            size="sm"
            onClick={() => onSnooze(snoozeUntil(o.id, new Date()))}
          >
            {o.label}
          </Button>
        ))}
        <span className="hp-snooze__hint">It comes back sooner if something new arrives.</span>
      </div>
    </Popover>
  );
}

/** One full-width card: icon, big title, detail, "View" at the right. */
function AlertCard({
  testId,
  severity,
  icon,
  title,
  detail,
  href,
  search,
  action,
  onSnooze,
}: {
  testId: string;
  severity: 'urgent' | 'attention';
  icon: ReactNode;
  title: string;
  detail: string | null;
  /** No link (the person cannot act on it here). */
  href: string | null;
  search?: Record<string, string>;
  action: string;
  onSnooze: (until: Date) => void;
}) {
  const body = (
    <>
      <span className="hp-card__icon">{icon}</span>
      <span className="hp-card__text">
        <span className="hp-card__title">
          {severity === 'urgent' ? <span className="vel-visually-hidden">Urgent: </span> : null}
          {title}
        </span>
        {detail ? <span className="hp-card__detail">{detail}</span> : null}
      </span>
      {href ? (
        <span className="hp-card__go">
          {action}
          <ChevronRight size={18} aria-hidden />
        </span>
      ) : null}
    </>
  );
  return (
    <li className="hp-card" data-severity={severity} data-testid={testId}>
      {href ? (
        <Link to={href as never} search={keepOffice(search ?? {})} className="hp-card__main">
          {body}
        </Link>
      ) : (
        <div className="hp-card__main">{body}</div>
      )}
      <SnoozeCorner label={title} onSnooze={onSnooze} />
    </li>
  );
}

/** "N people are waiting on us", a row per kind. */
function WaitingCard({
  rows,
  nameOf,
  officesInView,
  onSnooze,
}: {
  rows: HomePrompt[];
  nameOf: (id: string) => string | undefined;
  officesInView: number;
  onSnooze: (until: Date) => void;
}) {
  const now = Date.now();
  const urgent = rows.some((r) => r.severity === 'urgent');
  const title = waitingTitle(rows);
  const oldest = rows.reduce<string | null>(
    (o, r) => (r.oldestAt && (o === null || r.oldestAt < o) ? r.oldestAt : o),
    null,
  );
  return (
    <li
      className="hp-card hp-card--group"
      data-severity={urgent ? 'urgent' : 'attention'}
      data-testid="home-prompts-waiting"
    >
      <div className="hp-group__head">
        <span className="hp-group__dot" aria-hidden />
        <h2 className="hp-card__title hp-group__title">
          {urgent ? <span className="vel-visually-hidden">Urgent: </span> : null}
          {title}
        </h2>
        {oldest ? (
          <span className="hp-group__meta">longest wait {ageLabel(oldest, now)}</span>
        ) : null}
      </div>
      <ul className="hp-rows">
        {rows.map((p) => {
          const split = officeSplitLabel(p.byOffice, nameOf, officesInView);
          const sub = [subline(p, now), split].filter(Boolean).join(' · ');
          return (
            <li key={p.key} data-testid={`home-prompt-${p.key}`} data-severity={p.severity}>
              <Link
                to={p.href as never}
                search={keepOffice(p.search)}
                className="hp-row"
                aria-label={`${PROMPT_META[p.key].title(p.count)}. ${PROMPT_META[p.key].action}`}
              >
                <span className="hp-row__icon">{ICON[p.key](18)}</span>
                <span className="hp-row__text">
                  <span className="hp-row__label">
                    {p.severity === 'urgent' ? (
                      <span className="vel-visually-hidden">Urgent: </span>
                    ) : null}
                    {PROMPT_META[p.key].title(p.count)}
                  </span>
                  {sub ? <span className="hp-row__detail">{sub}</span> : null}
                </span>
                <span className="hp-row__go">
                  <span className="hp-row__action">{PROMPT_META[p.key].action}</span>
                  <ChevronRight size={18} aria-hidden />
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
      <SnoozeCorner label={title} onSnooze={onSnooze} />
    </li>
  );
}

/** One count chip: icon, bold count, noun; a link with a tooltip. */
function Chip({
  testId,
  severity,
  icon,
  count,
  noun,
  tip,
  href,
  search,
}: {
  testId: string;
  severity: 'urgent' | 'attention';
  icon: ReactNode;
  count: number;
  noun: string;
  tip: string;
  /** No link: the person cannot act on it here (tooltip says who can). */
  href: string | null;
  search?: Record<string, string>;
}) {
  const body = (
    <>
      <span className="hp-chip__icon">{icon}</span>
      <b className="hp-chip__n vel-num">{formatNumber(count)}</b>
      <span className="hp-chip__noun">{noun}</span>
    </>
  );
  return (
    <li className="hp-chips__item" data-testid={testId} data-severity={severity}>
      <Tooltip content={tip} side="bottom">
        {href ? (
          <Link
            to={href as never}
            search={keepOffice(search ?? {})}
            className="hp-chip"
            aria-label={`${severity === 'urgent' ? 'Urgent: ' : ''}${tip}`}
          >
            {body}
          </Link>
        ) : (
          <span
            className="hp-chip"
            tabIndex={0}
            role="note"
            aria-label={`${severity === 'urgent' ? 'Urgent: ' : ''}${tip}`}
          >
            {body}
          </span>
        )}
      </Tooltip>
    </li>
  );
}

export function HomePrompts({
  role,
  currentNote = false,
}: {
  role: DashboardRole;
  /** Home shows another day: say these are about right now, not that day. */
  currentNote?: boolean;
}) {
  const q = useHomePrompts();
  const fresh = useFreshness();
  const scope = useOfficeScope();
  const canFixSync = usePermission('settings.manage');
  const prefs = usePrefs();
  const navigate = useNavigate();
  const layout = prefs.homeLayout;
  const now = Date.now();
  const all = q.data?.prompts ?? [];
  const { shown, snoozed } = rankPrompts(all, role, layout.snoozed, now);
  const cards = toCards(shown);
  const names = new Map(scope.offices.map((o) => [o.id, o.shortName || o.name]));
  const nameOf = (id: string) => names.get(id);
  const officesInView = scope.locationIds.length;

  // PMS sync: an office whose data stopped updating (every role: a stale
  // schedule misleads everyone). Only people who can fix it get the link.
  const sync = syncProblems(fresh.data?.offices ?? [], now);
  const syncItem: Snoozable = { key: SYNC_CARD_KEY, count: sync.length };
  const syncSnoozed = sync.length > 0 && isSnoozed(syncItem, layout.snoozed, now);
  const showSync = sync.length > 0 && !syncSnoozed;

  // Pop-up for new urgent items (not on the first load, not for snoozed ones).
  const prev = useRef<HomePrompt[] | null>(null);
  const toastOn = layout.toastUrgent !== false;
  useEffect(() => {
    if (!q.data) return;
    const ranked = rankPrompts(q.data.prompts, role, prefs.homeLayout.snoozed, Date.now()).shown;
    const fresh = newUrgent(prev.current, ranked);
    prev.current = ranked;
    if (!toastOn) return;
    for (const p of fresh.slice(0, 2)) {
      toast({
        title: PROMPT_META[p.key].title(p.count),
        ...(subline(p) ? { description: subline(p) as string } : {}),
        tone: 'danger',
        action: {
          label: PROMPT_META[p.key].action,
          onAction: () => void navigate({ to: p.href as never, search: keepOffice(p.search) }),
        },
      });
    }
    // Only a new answer drives the pop-up.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data?.generatedAt]);

  const snoozedCount = snoozed.length + (syncSnoozed ? 1 : 0);
  if (shown.length === 0 && !showSync && snoozedCount === 0) return null;
  const save = (items: Snoozable | readonly Snoozable[], until: Date) =>
    prefs.set({ homeLayout: snooze(layout, items, until, Date.now()) });

  // Not updating (error) or only delayed (warning), on the connector-interval rule.
  const syncState = sync.some((s) => s.level === 'error') ? 'not updating' : 'delayed';
  const syncSeverity = syncState === 'not updating' ? 'urgent' : 'attention';
  const syncTitle =
    sync.length === 1
      ? `${nameOf(sync[0]?.locationId ?? '') ?? 'An office'}: PMS data is ${syncState}`
      : `PMS data is ${syncState} at ${sync.length} offices`;
  const syncLast = sync
    .map((s) => s.lastSyncedAt)
    .filter((x): x is string => x !== null)
    .sort()[0];
  const syncDetail = [
    sync.length > 1 ? sync.map((s) => nameOf(s.locationId) ?? 'Office').join(', ') : null,
    syncLast ? `last sync ${relativeAge(syncLast, now)}` : null,
    'schedules and balances may be out of date',
    canFixSync ? null : 'tell your office manager',
  ]
    .filter(Boolean)
    .join(' · ');

  const expanded = layout.promptsExpanded === true;
  const severity = stripSeverity(
    shown,
    showSync ? sync.filter((s) => s.level === 'error').length : 0,
  );
  const windowHours = q.data?.windowHours;
  const listId = 'home-prompts-detail';
  const everything: Snoozable[] = [...shown, ...(showSync ? [syncItem] : [])];
  const age = (iso: string) => ageLabel(iso, now);

  return (
    <section
      className="hp"
      aria-label="Waiting on the office"
      data-testid="home-prompts"
      data-expanded={expanded ? 'true' : 'false'}
    >
      {everything.length > 0 ? (
        <TooltipProvider>
          <div className="hp-strip" data-severity={severity} data-testid="home-prompts-strip">
            <span className="hp-strip__lead">
              <span className="hp-strip__dot" aria-hidden />
              <span className="hp-strip__title">
                {severity === 'urgent' ? (
                  <span className="vel-visually-hidden">Urgent: </span>
                ) : null}
                Waiting on us
                {currentNote ? (
                  <span
                    className="hd-now"
                    title="These are about right now; they do not follow the day picked above"
                    data-testid="home-prompts-now"
                  >
                    Right now
                  </span>
                ) : null}
              </span>
              {windowHours && shown.some((p) => p.since) ? (
                <span className="hp-strip__window">calls: last {windowHours} h</span>
              ) : null}
            </span>
            <ul className="hp-chips" aria-live="polite">
              {showSync ? (
                <Chip
                  testId="home-chip-pms_sync"
                  severity={syncSeverity}
                  icon={<DatabaseZap size={14} aria-hidden />}
                  count={sync.length}
                  noun={syncChipNoun(sync.length, syncState === 'delayed')}
                  tip={`${syncTitle}: ${syncDetail}`}
                  href={canFixSync ? '/settings/connectors' : null}
                />
              ) : null}
              {shown.map((p) => (
                <Chip
                  key={p.key}
                  testId={`home-chip-${p.key}`}
                  severity={p.severity}
                  icon={ICON[p.key](14)}
                  count={p.count}
                  noun={CHIP_NOUN[p.key](p.count)}
                  tip={chipTooltip(p, {
                    split: officeSplitLabel(p.byOffice, nameOf, officesInView),
                    age,
                    windowHours,
                    now,
                  })}
                  href={p.href}
                  search={p.search}
                />
              ))}
            </ul>
            <span className="hp-strip__tools">
              <SnoozeCorner
                inline
                label="everything waiting"
                onSnooze={(until) => save(everything, until)}
              />
              <button
                type="button"
                className="hp-strip__toggle"
                aria-expanded={expanded}
                aria-controls={listId}
                onClick={() => prefs.set({ homeLayout: { ...layout, promptsExpanded: !expanded } })}
              >
                <span>{expanded ? 'Less' : 'Details'}</span>
                <ChevronDown size={16} aria-hidden className="hp-strip__chev" />
              </button>
            </span>
          </div>
        </TooltipProvider>
      ) : null}
      {expanded && everything.length > 0 ? (
        <ul className="hp-list" id={listId}>
          {showSync ? (
            <AlertCard
              testId="home-prompt-pms_sync"
              severity={syncSeverity}
              icon={<DatabaseZap size={22} aria-hidden />}
              title={syncTitle}
              detail={syncDetail}
              href={canFixSync ? '/settings/connectors' : null}
              action="Check sync"
              onSnooze={(until) => save(syncItem, until)}
            />
          ) : null}
          {cards.ops.map((p) => (
            <AlertCard
              key={p.key}
              testId={`home-prompt-${p.key}`}
              severity={p.severity}
              icon={ICON[p.key](22)}
              title={PROMPT_META[p.key].title(p.count)}
              detail={
                [p.detail, officeSplitLabel(p.byOffice, nameOf, officesInView)]
                  .filter(Boolean)
                  .join(' · ') || null
              }
              href={p.href}
              search={p.search}
              action={PROMPT_META[p.key].action}
              onSnooze={(until) => save(p, until)}
            />
          ))}
          {cards.waiting.length > 0 ? (
            <WaitingCard
              rows={cards.waiting}
              nameOf={nameOf}
              officesInView={officesInView}
              onSnooze={(until) => save(cards.waiting, until)}
            />
          ) : null}
          {cards.texts ? (
            <AlertCard
              testId="home-prompt-texts"
              severity={cards.texts.severity}
              icon={ICON.texts(22)}
              title={PROMPT_META.texts.title(cards.texts.count)}
              detail={
                [
                  subline(cards.texts, now),
                  officeSplitLabel(cards.texts.byOffice, nameOf, officesInView),
                ]
                  .filter(Boolean)
                  .join(' · ') || null
              }
              href={cards.texts.href}
              search={cards.texts.search}
              action={PROMPT_META.texts.action}
              onSnooze={(until) => cards.texts && save(cards.texts, until)}
            />
          ) : null}
        </ul>
      ) : null}
      {snoozedCount > 0 ? (
        <p className="hp-foot">
          <button
            type="button"
            className="hp-foot__link"
            onClick={() => prefs.set({ homeLayout: unsnoozeAll(layout) })}
          >
            Show {snoozedCount} snoozed
          </button>
        </p>
      ) : null}
    </section>
  );
}
